import type { JobCategory, ReadinessLevel, RiskFlag, RiskFlagType } from "../types";

const keywordMap: Record<RiskFlagType, string[]> = {
  ACCESS: ["attic", "crawl", "crawlspace", "locked", "gate", "roof access", "narrow"],
  ELECTRICAL: ["electrical", "breaker", "outlet", "panel", "wire", "wiring", "voltage"],
  HEIGHTS: ["ladder", "roof", "gutter", "second story", "high", "tree", "eaves"],
  HEAVY_LIFT: ["heavy", "appliance", "washer", "dryer", "piano", "concrete", "safe"],
  PERMIT: ["permit", "structural", "load bearing", "sub-panel", "subpanel", "gas line"],
  PETS: ["dog", "cat", "pet", "animals"],
  PLUMBING: ["plumbing", "pipe", "leak", "water shutoff", "toilet", "sink", "copper"],
  STRUCTURAL: ["structural", "load bearing", "framing", "foundation", "beam", "joist"],
  UNKNOWN_SCOPE: ["maybe", "not sure", "unknown", "figure it out", "whatever", "etc"],
  WATER_DAMAGE: ["flood", "water damage", "burst", "standing water", "mold"],
};

const flagCopy: Record<RiskFlagType, { label: string; guidance: string }> = {
  ACCESS: {
    label: "Access constraint",
    guidance:
      "Confirm entry, work area clearance, parking, pets, and any tight-space access before arrival.",
  },
  ELECTRICAL: {
    label: "Electrical risk",
    guidance:
      "Work that modifies wiring, panels, or circuits may require a licensed electrician and permits.",
  },
  HEIGHTS: {
    label: "Heights risk",
    guidance:
      "Confirm ladder requirements, surface conditions, roof pitch, and fall protection before acceptance.",
  },
  HEAVY_LIFT: {
    label: "Heavy lift",
    guidance: "Confirm item weight, stairs, path width, and whether a second person is required.",
  },
  PERMIT: {
    label: "Permit likely",
    guidance:
      "Permit-controlled work should be verified with the local authority before work starts.",
  },
  PETS: {
    label: "Pets on site",
    guidance: "Pets should be secured before arrival and kept clear of the work area.",
  },
  PLUMBING: {
    label: "Plumbing risk",
    guidance: "Confirm whether work is basic repair or licensed plumbing work before acceptance.",
  },
  STRUCTURAL: {
    label: "Structural risk",
    guidance: "Structural changes require qualified review and may require licensed trade work.",
  },
  UNKNOWN_SCOPE: {
    label: "Details unclear",
    guidance:
      "The customer must clarify the work before a contractor can make a sound go or no-go decision.",
  },
  WATER_DAMAGE: {
    label: "Water damage",
    guidance:
      "Confirm source control, active leak status, mold concerns, and whether emergency service is required.",
  },
};

const categoryFlags: Partial<Record<JobCategory, RiskFlagType[]>> = {
  ELECTRICAL: ["ELECTRICAL", "PERMIT"],
  PLUMBING: ["PLUMBING"],
  MOVING: ["HEAVY_LIFT"],
  LANDSCAPE: ["HEIGHTS"],
  PROPERTY_MAINTENANCE: ["ACCESS"],
};

function severityFor(type: RiskFlagType): ReadinessLevel {
  if (["ELECTRICAL", "PERMIT", "STRUCTURAL", "WATER_DAMAGE"].includes(type)) {
    return "RED";
  }

  if (["HEIGHTS", "PLUMBING", "HEAVY_LIFT"].includes(type)) {
    return "AMBER";
  }

  return "GREEN";
}

export function makeRiskFlag(type: RiskFlagType): RiskFlag {
  return {
    type,
    severity: severityFor(type),
    label: flagCopy[type].label,
    guidance: flagCopy[type].guidance,
  };
}

export function analyzeRisk(text: string, category: JobCategory): RiskFlag[] {
  const haystack = text.toLowerCase();
  const detected = new Set<RiskFlagType>(categoryFlags[category] ?? []);

  for (const [type, keywords] of Object.entries(keywordMap) as [RiskFlagType, string[]][]) {
    if (keywords.some((keyword) => haystack.includes(keyword))) {
      detected.add(type);
    }
  }

  return Array.from(detected).map(makeRiskFlag);
}

export function hasLicensedTradeRisk(flags: RiskFlag[]): boolean {
  return flags.some((flag) => ["ELECTRICAL", "PERMIT", "STRUCTURAL"].includes(flag.type));
}
